import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';
import { clearEntitlementsCache } from '../hooks/useFeature';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Decodifica payload JWT solo para UI (no valida firma). */
function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }
}

function tokenLooksImpersonating(token) {
  const payload = decodeJwtPayload(token);
  return !!(payload?.impersonator_id || payload?.impersonated_by);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isImpersonating, setIsImpersonating] = useState(() => {
    const t = localStorage.getItem('token');
    return localStorage.getItem('impersonating') === 'true' || tokenLooksImpersonating(t);
  });

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (savedToken && savedUser) {
        setToken(savedToken);
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          setUser(null);
        }
      }

      const impersonating =
        localStorage.getItem('impersonating') === 'true' || tokenLooksImpersonating(savedToken);
      if (impersonating && localStorage.getItem('impersonating') !== 'true') {
        localStorage.setItem('impersonating', 'true');
      }
      setIsImpersonating(impersonating);

      if (savedToken) {
        try {
          const me = await authService.getMe();
          if (me?.data) {
            localStorage.setItem('user', JSON.stringify(me.data));
            setUser(me.data);
          }
        } catch (e) {
          if (e.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('impersonating');
            localStorage.removeItem('impersonated_user');
            localStorage.removeItem('original_admin_token');
            setToken(null);
            setUser(null);
            setIsImpersonating(false);
          }
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authService.login({ email, password });
    const data = response.data || {};

    if (data.mfa_required && data.mfa_token) {
      return { mfaRequired: true, mfaToken: data.mfa_token };
    }
    if (data.mfa_enrollment_required && data.mfa_token) {
      return { mfaEnrollmentRequired: true, mfaToken: data.mfa_token };
    }

    const { access_token, user: userData } = data;
    if (!access_token || !userData) {
      throw new Error('Respuesta de login incompleta');
    }

    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user');
    localStorage.removeItem('original_admin_token');
    clearEntitlementsCache();

    setToken(access_token);
    setUser(userData);
    setIsImpersonating(false);

    return userData;
  };

  const applySession = (access_token, userData) => {
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user');
    localStorage.removeItem('original_admin_token');
    clearEntitlementsCache();
    setToken(access_token);
    setUser(userData);
    setIsImpersonating(false);
    return userData;
  };

  const completeMfaLogin = async (mfaToken, code) => {
    const response = await authService.mfaChallenge(code, mfaToken);
    const { access_token, user: userData } = response.data || {};
    if (!access_token || !userData) {
      throw new Error('Respuesta MFA incompleta');
    }
    return applySession(access_token, userData);
  };

  const completeMfaEnrollment = async (mfaToken, code) => {
    // setup debe haberse llamado antes; aquí solo confirma
    const response = await authService.mfaVerifyEnroll(code, mfaToken);
    const data = response.data || {};
    if (data.access_token && data.user) {
      applySession(data.access_token, data.user);
    } else if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    }
    return data;
  };

  const refreshUser = async () => {
    const me = await authService.getMe();
    if (me?.data) {
      localStorage.setItem('user', JSON.stringify(me.data));
      setUser(me.data);
      return me.data;
    }
    return null;
  };

  const register = async (data) => {
    const response = await authService.register(data);
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));

    setToken(access_token);
    setUser(userData);

    return userData;
  };

  const claimCitizen = async (data) => {
    const response = await authService.claimCitizen(data);
    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));

    setToken(access_token);
    setUser(userData);

    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user');
    localStorage.removeItem('original_admin_token');
    clearEntitlementsCache();
    setToken(null);
    setUser(null);
    setIsImpersonating(false);
  };

  const stopImpersonation = useCallback(() => {
    const originalToken = localStorage.getItem('original_admin_token');

    // Siempre limpiar flags de impersonación
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user');
    setIsImpersonating(false);
    clearEntitlementsCache();

    if (originalToken) {
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('user'); // forzar refresh vía getMe
      window.location.href = '/backoffice/usuarios';
      return;
    }

    // Sin token admin original (sesión rota): salir limpio al login
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('original_admin_token');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  }, []);

  const isAuthenticated = !!token && !!user;
  const isSuperadmin = user?.is_superadmin === true || user?.role === 'platform_superadmin' || user?.role === 'superadmin';
  const isCitizen = user?.role === 'citizen';
  const isStaff = (user?.role && user.role !== 'citizen') || isSuperadmin;
  const isAdmin = user?.role === 'admin' || isSuperadmin;
  const isVentanilla = user?.role === 'ventanilla_unica';
  const isArchivist = user?.role === 'archivist';
  const isTitularArea = !!user?.es_titular_area;
  const isSecretariaParticular = !!user?.es_secretaria_particular;
  const isAsistenteArea = !!user?.es_asistente_area;
  const actuandoComoParticular = !!user?.actuando_como_particular;
  const perfilOficina = user?.perfil_oficina || null;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        completeMfaLogin,
        completeMfaEnrollment,
        refreshUser,
        register,
        claimCitizen,
        logout,
        isAuthenticated,
        isSuperadmin,
        isCitizen,
        isStaff,
        isAdmin,
        isVentanilla,
        isArchivist,
        isTitularArea,
        isSecretariaParticular,
        isAsistenteArea,
        actuandoComoParticular,
        perfilOficina,
        isImpersonating,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
